import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as RIGHT } from "../../../assets/images/icons/arrow-right.svg";

const SECTION_LABELS = {
  settings: "Settings",
  stores: "Stores",
  store: "Store",
  restaurants: "Restaurant",
  restaurant: "Restaurant",
  business: "Businesses",
  users: "Users",
  coupons: "Coupon",
  promo: "Promo Code",
  shippers: "Shippers",
  merchandise: "Merchandise",
  "merchandise-packages": "Merchandise Packages",
  carts: "Cart",
  food: "Food",
  "food-packages": "Food Packages",
  "restaurant-orders": "Restaurant Orders",
  "store-orders": "Store Orders",
  orders: "Orders",
  rewards: "Rewards",
  infractions: "Infraction",
  catalogue: "Catalogues",
  payments: "Payments",
  "orders-report": "Orders Report",
  "users-report": "Users Report",
  "feature-report": "Feature Report",
  "business-report": "Business Report",
  feature: "Feature",
  "delivery-modes": "Delivery Modes",
  report: "Reports",
  employees: "Employees",
};

const ACTION_LABELS = {
  create: "New",
  "create-order": "New",
  "create-promo": "New",
  "create-coupon": "New",
  "create-shipper": "New",
  invite: "Invite",
  edit: "Edit",
  details: "Details",
  statistics: "Statistics",
};

const KNOWN_STATUSES = new Set([
  "all",
  "active",
  "approved",
  "unapproved",
  "pending",
  "failure",
  "successful",
  "open",
  "closed",
  "shipped",
  "unshipped",
  "delivered",
  "cancelled",
  "processing",
]);

const MAX_OUTLET_LENGTH = 22;

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch (error) {
    return segment;
  }
}

function toTitleCase(value) {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isNumeric(value) {
  return /^[0-9]+$/.test(value);
}

function isIdLike(value) {
  return /^[a-f0-9-]{8,}$/i.test(value) || /^[0-9]{3,}$/.test(value);
}

function truncateLabel(label) {
  if (label.length <= MAX_OUTLET_LENGTH) {
    return label;
  }

  return `${label.slice(0, MAX_OUTLET_LENGTH - 1)}...`;
}

function getHomeRoute(segments, hasOutletContext) {
  if (hasOutletContext && segments[1]) {
    return `/admin/${segments[1]}/dashboard`;
  }

  return "/admin/dashboard";
}

function getSectionIndex(segments, hasOutletContext) {
  return hasOutletContext ? 2 : 1;
}

function isOutletContext(segments) {
  const secondSegment = segments[1];
  return Boolean(secondSegment && !SECTION_LABELS[secondSegment]);
}

function buildCrumbs(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const hasOutletContext = isOutletContext(segments);
  const sectionIndex = getSectionIndex(segments, hasOutletContext);
  const sectionKey = segments[sectionIndex];
  const crumbs = [
    {
      label: "Home",
      to: getHomeRoute(segments, hasOutletContext),
    },
  ];

  if (hasOutletContext) {
    const outletName = truncateLabel(decodeSegment(segments[1]));
    crumbs.push({
      label: outletName || "Outlet",
      to: `/admin/${segments[1]}`,
    });
  }

  if (sectionKey) {
    crumbs.push({
      label: SECTION_LABELS[sectionKey] || toTitleCase(sectionKey),
      to: `/${segments.slice(0, sectionIndex + 1).join("/")}`,
    });
  }

  for (let index = sectionIndex + 1; index < segments.length; index += 1) {
    const segment = segments[index];
    const previousSegment = segments[index - 1];
    const nextSegment = segments[index + 1];

    if (segment === "admin" || segment === sectionKey) {
      // Hidden technical segments.
      // eslint-disable-next-line no-continue
      continue;
    }

    if (isNumeric(segment)) {
      // Treat trailing numeric tokens as page numbers.
      // eslint-disable-next-line no-continue
      continue;
    }

    if (segment === "details" || segment === "single-infraction") {
      crumbs.push({
        label: "Details",
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (segment.startsWith("single-")) {
      crumbs.push({
        label: `${toTitleCase(segment.replace("single-", ""))} Details`,
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (segment === "broadcast-notification") {
      crumbs.push({
        label: "Notify",
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (segment === "kyc-verification") {
      crumbs.push({
        label: "KYC",
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (ACTION_LABELS[segment]) {
      crumbs.push({
        label: ACTION_LABELS[segment],
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (KNOWN_STATUSES.has(segment)) {
      crumbs.push({
        label: toTitleCase(segment),
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    if (
      isIdLike(segment) &&
      (ACTION_LABELS[previousSegment] ||
        previousSegment === "details" ||
        previousSegment?.startsWith("single-") ||
        nextSegment === "edit")
    ) {
      // Avoid exposing raw ids unless they carry navigation meaning.
      // eslint-disable-next-line no-continue
      continue;
    }

    if (SECTION_LABELS[segment]) {
      crumbs.push({
        label: SECTION_LABELS[segment],
        to: `/${segments.slice(0, index + 1).join("/")}`,
      });
      // eslint-disable-next-line no-continue
      continue;
    }

    crumbs.push({
      label: toTitleCase(decodeSegment(segment)),
      to: `/${segments.slice(0, index + 1).join("/")}`,
    });
  }

  if (crumbs.length > 4) {
    return [crumbs[0], crumbs[1], { label: "...", to: null }, crumbs.at(-1)];
  }

  return crumbs;
}

export default function AdminBreadCrumbs() {
  const location = useLocation();
  const navigate = useNavigate();

  const isDashboard = location.pathname.includes("/dashboard");
  const crumbs = buildCrumbs(location.pathname);

  return (
    <div>
      {!isDashboard && (
        <div className="flex flex-wrap items-center">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            const isClickable = !isLast && Boolean(crumb.to);

            return (
              <div className="flex py-7 px-2 items-center" key={`${crumb.label}-${index}`}>
                <button
                  type="button"
                  onClick={() => {
                    if (isClickable) {
                      navigate(crumb.to);
                    }
                  }}
                  className={`text-[#4D4D4D] dark:text-[#BCBCBC] capitalize ${
                    isClickable ? "cursor-pointer" : "cursor-default"
                  }`}
                  disabled={!isClickable}
                >
                  {crumb.label}
                </button>
                {!isLast && <RIGHT className="h-6 w-6" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
